import {
  Box,
  Heading,
  Text,
  Flex,
  HStack,
  VStack,
  Grid,
  Spinner,
  Center,
  useBreakpointValue,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { ReportsService } from "@/client"
import { FiSearch } from "react-icons/fi"
import { TbFileReport } from "react-icons/tb"
import ReportSetting from "@/components/Reports/ReportSetting"
import FeedBadges from "@/components/Reports/FeedBadges"
import { ActionsMenuReportJob } from "@/components/Reports/ActionsMenuReportJob"
import Pagination from "@/components/Common/Pagination"
import { ProcessStateTag } from "@/components/ui/state-tag"
import {
  ReportSchedule,
  ReportType,
  getScheduleLabel,
  getReportTypeLabel,
} from "@/types/report"

import SearchInput from "@/components/Common/SearchInput"
import SortButtons from "@/components/Common/SortButtons"

export const Route = createFileRoute("/_layout/reports")({
  component: ReportsSection,
})

function ReportsSection() {
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchKeyword, setSearchKeyword] = useState("")
  const [sortKey, setSortKey] = useState("created_at")
  const [sortOrder, setSortOrder] = useState("desc")
  const [page, setPage] = useState(1)
  const itemsPerPage = 8

  const { data: reportJobs, isLoading } = useQuery({
    queryKey: ["reportJobs", sortKey, sortOrder],
    queryFn: () => ReportsService.listReportJobs({ sortKey: sortKey, sortOrder: sortOrder }),
    refetchInterval: 5000,
    refetchIntervalInBackground: false
  })

  const filteredJobs = reportJobs?.filter((job) =>
    job.report_job_name.toLowerCase().includes(searchKeyword.toLowerCase())
  ) || []

  const paginatedJobs = filteredJobs.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  )
  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage)
  const isMobile = useBreakpointValue({ base: true, sm: false });
  
  if (isLoading) {
    return (
      <Center py={10}>
        <Spinner />
      </Center>
    )
  }

  return (
    <Box display="flex" flexDirection="column" height="100%">
      {isMobile ? (
        <VStack align="start">
          <HStack alignItems="center">
            <Heading size="lg">리포트 생성 관리</Heading>
            <TbFileReport />
            <ReportSetting />
          </HStack>

          <HStack w="100%" pb="4">
            <SearchInput value={searchKeyword} onChange={(val) => {
                setSearchKeyword(val)
                setPage(1)
              }}
            />
            <SortButtons sortKey={sortKey} sortOrder={sortOrder} onSortChange={(key, order) => {
                setSortKey(key)
                setSortOrder(order)
                setPage(1)
              }}
            />
          </HStack>
        </VStack>
      ) : (
        <Flex justifyContent="space-between" alignItems="center" mb={2}>
          <Heading size="lg">
            <HStack alignItems="baseline">
              리포트 생성 관리
              <TbFileReport />
              <ReportSetting />
            </HStack>
          </Heading>

          <HStack gap={4}>
            <SearchInput value={searchKeyword} onChange={(val) => {
              setSearchKeyword(val)
              setPage(1)
            }} />
            <SortButtons sortKey={sortKey} sortOrder={sortOrder} onSortChange={(key, order) => {
              setSortKey(key)
              setSortOrder(order)
              setPage(1)
            }} />
          </HStack>
        </Flex>
      )}
      {!reportJobs || reportJobs.length === 0 ? (
        <VStack pt={10} gap={4} align="center">
          <FiSearch size={32} />
          <Text fontSize="lg">리포트가 없습니다</Text>
          <Text fontSize="sm" color="gray.500">
            "리포트 생성" 버튼을 눌러 새 리포트를 시작해보세요.
          </Text>
        </VStack>
      ) : (
        <Box
          flex="1" // 남은 높이 모두 차지
          display="flex"
          flexDirection="column"
          minHeight="0" // 중요: overflow 컨테이너가 자식인 경우 필요
        >
          <Box flex="1">
            <Grid
              templateColumns={{
                base: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              }}
              gap={6}
              mb={6}
            >
              {paginatedJobs.map((job) => (
                <Box
                  key={job.id}
                  p={4}
                  position="relative"
                  borderWidth={1}
                  borderRadius="md"
                  boxShadow="sm"
                  _hover={{ shadow: "md", cursor: "pointer" }}
                  onClick={() => navigate({ to: "/reportJob/$reportJobId", params: { reportJobId: job.id } })}
                  overflow="hidden"
                  display="flex"
                  flexDirection="column"
                >
                  <Flex justifyContent="space-between" alignItems="center">
                    <Text
                      fontWeight="semibold"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      flex="1"
                      mr={4}
                    >
                      {job.report_job_name}
                    </Text>
                    <HStack alignItems="center">
                      <Text fontSize="xs" color="gray.400">
                        <ProcessStateTag state={job?.state || "idle"} _disabled={{ opacity: 0.5 }} />
                      </Text>
                      <Box onClick={(e) => e.stopPropagation()}>
                        <ActionsMenuReportJob reportJob={job} />
                      </Box>
                    </HStack>
                  </Flex>

                  <Box maxH="2.5em" overflow="hidden">
                    <Text
                      fontSize="sm" color="gray.500"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {job.description || "설명 없음"}
                    </Text>
                  </Box>

                  {(job.feeds && job.feeds.length > 0) ? (
                    <FeedBadges feeds={job.feeds} />
                  ) : (
                    <Box mt={3} h="1.5rem"/>
                  )}

                  <Text mt={2} fontSize="xs" color="gray.400">
                    생성일: {new Date(job.created_at).toLocaleDateString()}
                  </Text>

                  <Flex justifyContent="space-between" fontSize="xs" color="gray.400">
                    <Text>주기: {getScheduleLabel(job.schedule as ReportSchedule)}</Text>
                    <Text>유형: {getReportTypeLabel(job.report_type as ReportType)}</Text>
                  </Flex>
                </Box>
              ))}
            </Grid>
          </Box>
          <Box py={3} borderTop="1px solid" borderColor="gray.200" bg="white" textAlign="center">
            <Pagination page={page} setPage={setPage} totalPages={totalPages} />
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default ReportsSection
